// frontend/src/components/product/swatches/productSwatches.js

import tinycolor from "tinycolor2";

const COLOUR_MAP = Object.freeze({
  black: "#111111",
  white: "#f8f8f6",
  ivory: "#f4efe2",
  cream: "#f2eadb",
  beige: "#d8c3a5",
  natural: "#d7c7ad",

  camel: "#b88a58",
  tan: "#b98b61",
  brown: "#6b4b35",
  chocolate: "#4a2f24",

  grey: "#8f9094",
  gray: "#8f9094",
  silver: "#bfc3c8",
  charcoal: "#4a4d52",

  navy: "#1f2a44",
  blue: "#4066a8",
  sky: "#bfd7f2",

  teal: "#0f766e",
  turquoise: "#40b7ad",
  aqua: "#67cfd1",

  red: "#a1272d",
  burgundy: "#6e1f2a",
  wine: "#6a2230",

  pink: "#d9a6b0",
  blush: "#e7c7c2",
  "blush pink": "#e8bec5",
  coral: "#d66a5c",
  peach: "#e6a37a",

  green: "#5d7a63",
  "jungle green": "#1f5f4a",
  "persian green": "#00a693",
  olive: "#6b6a3f",
  sage: "#a8b39d",
  emerald: "#0f6a50",
  mint: "#a9d8bd",
  lime: "#c7d840",

  yellow: "#d9b43b",
  mustard: "#b8871b",
  gold: "#c8a44d",
  brass: "#b08a38",
  bronze: "#8f6534",
  copper: "#b46a3c",

  orange: "#c46a2d",
  rust: "#9a4a2d",

  purple: "#6c4c7d",
  lilac: "#b8a7d1",
  lavender: "#b7a4d8",
  mauve: "#9d7894",

  stone: "#b8ad9d",
  taupe: "#9f8f81",
});

const IGNORED_MULTI_TOKENS = new Set([
  "multi",
  "multicolour",
  "multicolor",
  "mixed",
]);

export function safeText(value = "") {
  return String(value ?? "").trim();
}

export function normalizeToken(value = "") {
  return safeText(value)
    .toLowerCase()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function titleCase(value = "") {
  return safeText(value)
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export function uniqueValues(values = []) {
  const seen = new Set();
  const output = [];

  (Array.isArray(values) ? values : []).forEach((value) => {
    const text = safeText(value);
    const key = normalizeToken(text);

    if (!text || !key || seen.has(key)) return;

    seen.add(key);
    output.push(text);
  });

  return output;
}

function getImageUrl(value) {
  if (typeof value === "string") return safeText(value);
  if (!value || typeof value !== "object") return "";

  return safeText(
    value.url ||
      value.src ||
      value.image ||
      value.imageUrl ||
      value.secure_url ||
      value.secureUrl ||
      value.thumbnail ||
      value.thumbnailUrl ||
      value.featuredImage ||
      value.featuredImageUrl ||
      value.swatchImage ||
      value.swatchImageUrl ||
      "",
  );
}

function hasOwn(value, key) {
  return Boolean(value) && Object.prototype.hasOwnProperty.call(value, key);
}

function getWords(value = "") {
  return normalizeToken(value)
    .split(" ")
    .map((word) => word.trim())
    .filter(Boolean);
}

function isHexColour(value = "") {
  return /^#([0-9a-f]{3}){1,2}$/i.test(safeText(value));
}

function isKnownColourPhrase(value = "") {
  return Boolean(COLOUR_MAP[normalizeToken(value)]);
}

function splitColourText(value = "") {
  const text = safeText(value);
  if (!text) return [];

  if (isHexColour(text)) return [text];

  /*
   * Important:
   * Exact known compound shades must stay as ONE colour.
   *
   * Examples:
   *   "Blush pink"     -> ["Blush pink"]
   *   "Jungle green"   -> ["Jungle green"]
   *   "Persian green"  -> ["Persian green"]
   */
  if (isKnownColourPhrase(text)) return [text];

  /*
   * Explicit separators mean separate colours inside one colourway.
   *
   * Examples:
   *   "Gold, Red, Cream"           -> ["Gold", "Red", "Cream"]
   *   "Jungle green, Persian green" -> ["Jungle green", "Persian green"]
   */
  const delimiterParts = text
    .split(/,|;|\/|&|\+|\band\b/i)
    .map((part) => safeText(part))
    .filter(Boolean);

  if (delimiterParts.length > 1) {
    return delimiterParts.flatMap((part) => splitColourText(part));
  }

  /*
   * Last-resort heuristic:
   * If a phrase is not a known compound shade and contains multiple known
   * colour words, treat it as a mixed colourway.
   *
   * Example:
   *   "Blue Green" -> ["blue", "green"]
   */
  const words = getWords(text);
  const colourWords = words.filter(
    (word) => COLOUR_MAP[word] && !IGNORED_MULTI_TOKENS.has(word),
  );

  if (colourWords.length > 1) return colourWords;

  return [text];
}

export function expandColourValues(values = []) {
  const expanded = [];

  (Array.isArray(values) ? values : [values]).forEach((value) => {
    if (Array.isArray(value)) {
      expanded.push(...expandColourValues(value));
      return;
    }

    expanded.push(...splitColourText(value));
  });

  return uniqueValues(expanded);
}

export function getOptionColourValues(option = {}) {
  return expandColourValues([
    ...(Array.isArray(option?.colors) ? option.colors : []),
    ...(Array.isArray(option?.colours) ? option.colours : []),
    ...(Array.isArray(option?.colorway) ? option.colorway : []),
    ...(Array.isArray(option?.colourway) ? option.colourway : []),
    ...(Array.isArray(option?.colorwayColors) ? option.colorwayColors : []),
    ...(Array.isArray(option?.colourwayColors) ? option.colourwayColors : []),
    ...(Array.isArray(option?.colorwayColours) ? option.colorwayColours : []),
    ...(Array.isArray(option?.colourwayColours) ? option.colourwayColours : []),
    ...(Array.isArray(option?.palette) ? option.palette : []),
    option?.color,
    option?.colour,
    option?.colorName,
    option?.colourName,
  ]);
}

export function getProductColourValues(product = {}) {
  return expandColourValues([
    ...(Array.isArray(product?.colors) ? product.colors : []),
    ...(Array.isArray(product?.colours) ? product.colours : []),
    ...(Array.isArray(product?.colorway) ? product.colorway : []),
    ...(Array.isArray(product?.colourway) ? product.colourway : []),
    ...(Array.isArray(product?.colorwayColors) ? product.colorwayColors : []),
    ...(Array.isArray(product?.colourwayColors) ? product.colourwayColors : []),
    ...(Array.isArray(product?.colorwayColours) ? product.colorwayColours : []),
    ...(Array.isArray(product?.colourwayColours)
      ? product.colourwayColours
      : []),
    ...(Array.isArray(product?.palette) ? product.palette : []),
    product?.color,
    product?.colour,
    product?.colorName,
    product?.colourName,
  ]);
}

export function getSwatchValue(value = "") {
  const text = safeText(value);
  const normalized = normalizeToken(text);

  if (!text) return "#d7d0aa";
  if (isHexColour(text)) return text;

  if (COLOUR_MAP[normalized]) return COLOUR_MAP[normalized];

  const words = getWords(text);
  const firstKnownColour = words.find((word) => COLOUR_MAP[word]);

  if (firstKnownColour) return COLOUR_MAP[firstKnownColour];

  const parsed = tinycolor(text);
  if (parsed.isValid() && parsed.getAlpha() > 0) return text;

  return "#d7d0aa";
}

function getSwatchImage(source = {}) {
  return getImageUrl(
    source?.swatchImage ||
      source?.swatchImageUrl ||
      source?.swatch ||
      source?.image ||
      source?.imageUrl ||
      source?.thumbnail ||
      source?.thumbnailUrl ||
      source?.featuredImage ||
      source?.featuredImageUrl ||
      "",
  );
}

export function buildVisualSwatch({
  image = "",
  colours = [],
  fallback = "#d7d0aa",
  label = "",
} = {}) {
  const imageUrl = safeText(image);

  if (imageUrl) {
    return {
      kind: "image",
      value: imageUrl,
      label: safeText(label),
      colourCount: 0,
      rawValues: [],
    };
  }

  const colourValues = expandColourValues(colours);

  if (colourValues.length > 1) {
    return {
      kind: "multi",
      values: colourValues.map(getSwatchValue),
      rawValues: colourValues,
      label: safeText(label) || getReadableColourList(colourValues),
      colourCount: colourValues.length,
    };
  }

  if (colourValues.length === 1) {
    return {
      kind: "colour",
      value: getSwatchValue(colourValues[0]),
      rawValues: colourValues,
      label: safeText(label) || titleCase(colourValues[0]),
      colourCount: 1,
    };
  }

  return {
    kind: "colour",
    value: fallback,
    rawValues: [],
    label: safeText(label),
    colourCount: 0,
  };
}

export function getOptionVisualSwatch(option = {}) {
  return buildVisualSwatch({
    image: hasOwn(option, "swatchImage")
      ? getImageUrl(option.swatchImage)
      : getSwatchImage(option),
    colours: getOptionColourValues(option),
    label:
      option?.swatchLabel ||
      option?.colourwayLabel ||
      option?.colorwayLabel ||
      option?.selectionLabel ||
      option?.label ||
      option?.variantName ||
      option?.name ||
      "",
  });
}

export function getProductVisualSwatch(product = {}) {
  return buildVisualSwatch({
    image: getSwatchImage(product),
    colours: getProductColourValues(product),
    label:
      product?.colourwayLabel ||
      product?.colorwayLabel ||
      product?.colourLabel ||
      product?.colorLabel ||
      "",
  });
}

export function getVariantVisualSwatch(variant = {}) {
  return buildVisualSwatch({
    image: hasOwn(variant, "images")
      ? getImageUrl(Array.isArray(variant.images) ? variant.images[0] : "")
      : getSwatchImage(variant),
    colours: getOptionColourValues(variant),
    label:
      variant?.colourwayLabel ||
      variant?.colorwayLabel ||
      variant?.variantName ||
      variant?.name ||
      variant?.label ||
      "",
  });
}

function getReadableColourList(values = []) {
  return (Array.isArray(values) ? values : []).map(titleCase).join(", ");
}

function buildProductLevelColourway(product = {}) {
  const swatch = getProductVisualSwatch(product);

  if (!swatch?.colourCount && swatch?.kind !== "image") return null;

  const readableColours = getReadableColourList(swatch.rawValues);

  return {
    key: `product-${
      swatch.rawValues?.map(normalizeToken).join("-") || "swatch"
    }`,
    swatch,
    label: "",
    displayLabel: "",
    detailLabel: readableColours || swatch.label || "Colourway",
    ariaLabel: readableColours ? `Colourway: ${readableColours}` : "Colourway",
    isColourway: swatch.colourCount > 1 || swatch.kind === "image",
  };
}

function buildVariantColourway(variant = {}, index = 0) {
  const swatch = getVariantVisualSwatch(variant);

  if (!swatch?.colourCount && swatch?.kind !== "image") return null;

  const readableColours = getReadableColourList(swatch.rawValues);
  const variantLabel = safeText(
    variant?.variantName || variant?.name || variant?.label,
  );

  return {
    key:
      safeText(variant?._id || variant?.id || variant?.sku) ||
      `variant-${index}-${swatch.rawValues?.map(normalizeToken).join("-")}`,
    swatch,
    label: "",
    displayLabel: "",
    detailLabel: variantLabel || readableColours || swatch.label || "Colourway",
    ariaLabel:
      variantLabel && readableColours
        ? `${variantLabel}: ${readableColours}`
        : readableColours
          ? `Colourway: ${readableColours}`
          : variantLabel || swatch.label || "Colourway",
    isColourway: swatch.colourCount > 1 || swatch.kind === "image",
  };
}

export function getProductCardSwatchSummary(product = {}) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];

  const variantItems = product?.isVariant
    ? variants
        .map((variant, index) => buildVariantColourway(variant, index))
        .filter(Boolean)
    : [];

  const uniqueVariantItems = [];
  const seen = new Set();

  variantItems.forEach((item) => {
    const signature =
      item.swatch.kind === "image"
        ? `image:${item.swatch.value}`
        : `${item.swatch.kind}:${(item.swatch.rawValues || [])
            .map(normalizeToken)
            .join("|")}`;

    if (seen.has(signature)) return;

    seen.add(signature);
    uniqueVariantItems.push(item);
  });

  /*
   * Final card rule:
   *
   * Single product:
   *   one product-level swatch, even when product.colors has multiple colours.
   *
   * Variant product:
   *   one swatch per variant, each variant swatch may be solid, multi, or image.
   */
  const items = product?.isVariant
    ? uniqueVariantItems
    : [buildProductLevelColourway(product)].filter(Boolean);

  return {
    items,
    visibleItems: items.slice(0, 4),
    hiddenCount: Math.max(0, items.length - 4),

    label:
      product?.isVariant && items.length > 1
        ? `${items.length} colourways`
        : "",

    ariaLabel:
      product?.isVariant && items.length > 1
        ? `${items.length} colourways available`
        : items[0]?.ariaLabel || items[0]?.detailLabel || "Product colourway",
  };
}
